import React, { useRef, useState } from 'react';
import { Loader2, Upload, Save, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { RichTextEditor } from '../RichTextEditor';
import { LEVELS, errText } from './simUtils';
import { SimCodeEditor } from './SimCodeEditor';
import { SimQuestionsEditor } from './SimQuestionsEditor';
import { SimulationViewer } from '../simulation/SimulationViewer';

export const emptySim = (catId = '') => ({
  titleAr: '', titleEn: '', descAr: '', descEn: '', image: '', category_id: catId, level: 1, is_free: false, published: false,
  html: '', render_mode: '2d', aspect: '16:9', goalAr: '', instructionsAr: '', explainAr: '', diagramAr: '', extraAr: '', questions: [], actions: [],
});

const PARSED_KEYS = ['titleAr', 'descAr', 'category_id', 'level', 'render_mode', 'goalAr', 'instructionsAr', 'explainAr', 'diagramAr', 'extraAr', 'questions', 'actions'];
const snap = (f) => JSON.stringify(PARSED_KEYS.map(k => f[k]));

const inp = 'w-full h-10 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-blue-500';
const Field = ({ label, children }) => <label className="block space-y-1"><span className="text-xs font-bold text-slate-600">{label}</span>{children}</label>;

const BasicsTab = ({ f, set, setF, categories }) => {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/media/image', fd);
      setF(x => ({ ...x, image: data.url }));
    } catch (e) { toast.error(errText(e, 'تعذّر رفع الصورة')); }
    setUploading(false);
  };
  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="العنوان (عربي)"><input data-testid="sim-form-title-ar" value={f.titleAr} onChange={set('titleAr')} className={inp} /></Field>
        <Field label="العنوان (إنجليزي — يُترجم تلقائياً إن تُرك فارغاً)"><input data-testid="sim-form-title-en" dir="ltr" value={f.titleEn} onChange={set('titleEn')} className={inp} /></Field>
      </div>
      <Field label="وصف مختصر"><textarea data-testid="sim-form-desc-ar" rows={2} value={f.descAr} onChange={set('descAr')} className={`${inp} h-auto py-2`} /></Field>
      <div className="grid sm:grid-cols-4 gap-3">
        <Field label="التصنيف"><select data-testid="sim-form-category" value={f.category_id} onChange={set('category_id')} className={inp}>
          <option value="">بدون تصنيف</option>{categories.map(c => <option key={c.id} value={c.id}>{c.nameAr}</option>)}</select></Field>
        <Field label="المستوى"><select data-testid="sim-form-level" value={f.level} onChange={set('level')} className={inp}>{LEVELS.map(l => <option key={l.value} value={l.value}>{l.ar}</option>)}</select></Field>
        <Field label="نوع المشهد"><select data-testid="sim-form-mode" value={f.render_mode} onChange={set('render_mode')} className={inp}><option value="2d">ثنائي الأبعاد 2D</option><option value="3d">ثلاثي الأبعاد 3D</option></select></Field>
        <Field label="نسبة أبعاد المشهد"><select data-testid="sim-form-aspect" value={f.aspect} onChange={set('aspect')} className={inp}>{['16:9', '4:3', '1:1', '21:9'].map(a => <option key={a}>{a}</option>)}</select></Field>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {f.image && <img src={f.image} alt="" className="w-28 h-16 object-cover rounded-lg border" data-testid="sim-form-image-preview" />}
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-blue-300 text-blue-600 text-sm font-semibold cursor-pointer hover:bg-blue-50">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}صورة البطاقة
          <input data-testid="sim-form-image-input" type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        <label className="inline-flex items-center gap-2 text-sm"><input data-testid="sim-form-free" type="checkbox" checked={f.is_free} onChange={set('is_free')} />مجانية للجميع</label>
      </div>
    </div>
  );
};

const ContentTab = ({ f, set, setF }) => (
  <div className="space-y-4">
    <Field label="هدف المحاكاة"><textarea data-testid="sim-form-goal" rows={2} value={f.goalAr} onChange={set('goalAr')} className={`${inp} h-auto py-2`} /></Field>
    <Field label="تعليمات المحاكاة (كل خطوة في سطر)"><textarea data-testid="sim-form-instructions" rows={4} value={f.instructionsAr} onChange={set('instructionsAr')} className={`${inp} h-auto py-2`} /></Field>
    {[['explainAr', 'تبويب «الشرح»'], ['diagramAr', 'تبويب «مخطط الدائرة»'], ['extraAr', 'تبويب «معلومات إضافية»']].map(([k, label]) => (
      <Field key={k} label={`${label} — يختفي إن تُرك فارغاً`}>
        <RichTextEditor value={f[k]} onChange={(v) => setF(x => ({ ...x, [k]: v }))} testId={`sim-form-${k}`} minHeight={100} />
      </Field>
    ))}
  </div>
);

export const SimulationForm = ({ initial, categories, onSave, onCancel }) => {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState(initial.id ? 'basics' : 'code');
  const [report, setReport] = useState(null);
  const baseline = useRef(snap(initial));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const analyze = async () => {
    setBusy('analyze');
    try {
      const { data } = await api.post('/simulations/parse-html', { html: f.html });
      setReport(data);
      const patch = { ...data.fields };
      if (data.questions.length) patch.questions = data.questions;
      if (data.actions.length) patch.actions = data.actions;
      const edited = snap(f) !== baseline.current;
      const overwrites = Object.keys(patch).some(k => JSON.stringify(f[k]) !== JSON.stringify(patch[k]) && JSON.stringify(f[k]) !== JSON.stringify(emptySim()[k]));
      if (Object.keys(patch).length && edited && overwrites
        && !window.confirm('عدّلت بعض الحقول يدوياً. التحليل سيستبدل الحقول الموجودة في بيانات HTML بقيمها. هل تريد المتابعة؟')) {
        setBusy(''); return;
      }
      const next = { ...f, ...patch };
      setF(next);
      baseline.current = snap(next);
      if (data.source === 'structured') toast.success(`تمت التعبئة: ${data.filled.length} حقول`);
    } catch (e) { toast.error(errText(e, 'تعذّر تحليل الكود')); }
    setBusy('');
  };

  const submit = async (published) => {
    if (!f.titleAr.trim()) { setTab('basics'); toast.error('اكتب عنوان المحاكاة'); return; }
    const bad = f.questions.findIndex(q => !q.textAr.trim());
    if (bad >= 0) { setTab('questions'); toast.error(`السؤال ${bad + 1} بدون نص`); return; }
    const noAns = f.questions.findIndex(q => q.options.some(o => o.ar.trim()) && !(q.options[q.correct]?.ar || '').trim());
    if (noAns >= 0) { setTab('questions'); toast.error(`السؤال ${noAns + 1}: حدّد الإجابة الصحيحة`); return; }
    setBusy(published ? 'publish' : 'draft');
    const questions = f.questions.map(q => {
      const kept = q.options.map((o, i) => ({ o, i })).filter(x => x.o.ar.trim());
      const correct = kept.findIndex(x => x.i === q.correct);
      return { ...q, options: kept.map(x => x.o), correct: kept.length ? Math.max(correct, 0) : -1 };
    });
    await onSave({ ...f, published, level: Number(f.level), questions, actions: [...new Set([...(f.actions || []), ...questions.map(q => q.step).filter(Boolean)])] });
    setBusy('');
  };

  return (
    <div data-testid="sim-form" dir="rtl" className="text-start min-w-0 w-full">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1 rounded-xl">
          {[['code', '1. كود HTML'], ['basics', 'الأساسيات'], ['content', 'التعليمات والشرح'], ['questions', `الأسئلة (${f.questions.length})`], ['preview', 'معاينة']].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`sim-form-tab-${v}`} className="data-[state=active]:bg-white">{l}</TabsTrigger>
          ))}
        </TabsList>
        <div className="max-h-[62vh] overflow-y-auto overflow-x-hidden pt-4 px-0.5 min-w-0">
          <TabsContent value="basics"><BasicsTab f={f} set={set} setF={setF} categories={categories} /></TabsContent>
          <TabsContent value="code"><SimCodeEditor value={f.html} onChange={(v) => setF(x => ({ ...x, html: v }))} onAnalyze={analyze} analyzing={busy === 'analyze'} report={report} /></TabsContent>
          <TabsContent value="content"><ContentTab f={f} set={set} setF={setF} /></TabsContent>
          <TabsContent value="questions"><SimQuestionsEditor questions={f.questions} actions={f.actions || []} onChange={(qs) => setF(x => ({ ...x, questions: qs }))} /></TabsContent>
          <TabsContent value="preview">
            <div data-testid="sim-form-preview">{f.html.trim() ? <SimulationViewer sim={f} /> : <p className="text-slate-500 text-center py-10">ألصق كود المحاكاة أولاً في تبويب «كود المحاكاة».</p>}</div>
          </TabsContent>
        </div>
      </Tabs>
      <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-slate-100 mt-3">
        <button type="button" data-testid="sim-form-cancel" onClick={onCancel} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">إلغاء</button>
        <button type="button" data-testid="sim-form-save-draft" disabled={!!busy} onClick={() => submit(false)} className="h-10 px-4 rounded-lg border border-blue-300 text-blue-700 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
          {busy === 'draft' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}حفظ كمسودة
        </button>
        <button type="button" data-testid="sim-form-save" disabled={!!busy} onClick={() => submit(true)} className="h-10 px-5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
          {busy === 'publish' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}حفظ ونشر
        </button>
      </div>
    </div>
  );
};
